/**
 * Motor de tema white-label (PR1). Converte a cor PRIMÁRIA restrita do tenant
 * numa rampa `--brand-50..950` (canais "R G B") e a aplica no <html> — as 176
 * classes `brand-*` do Tailwind temam sozinhas por essa indireção (ver
 * tokens.css + tailwind.config.ts).
 *
 * A rampa default do Patrium é a AFINADA à mão em tokens.css; só derivamos e
 * sobrescrevemos quando o tenant escolhe uma primária DIFERENTE do Azul Patrium
 * — assim a experiência Patrium continua pixel-perfect.
 */

export const AZUL_PATRIUM = '#2038E8'

type RGB = [number, number, number]

/**
 * "#RRGGBB" → [r,g,b] (0..255) ou null se inválido. Defensivo contra
 * undefined/vazio de propósito: chamadores como o preview de contraste do
 * marca-tenant-form leem o valor de um `watch()` do RHF, que passa por um
 * frame intermediário undefined entre o fetch resolver e o `reset()` do
 * formulário — nunca deve derrubar o render.
 */
export function hexParaRGB(hex: string | undefined | null): RGB | null {
  if (!hex) return null
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function mix(a: RGB, b: RGB, t: number): RGB {
  return [
    Math.round(a[0] * (1 - t) + b[0] * t),
    Math.round(a[1] * (1 - t) + b[1] * t),
    Math.round(a[2] * (1 - t) + b[2] * t),
  ]
}

const BRANCO: RGB = [255, 255, 255]
const PRETO: RGB = [0, 0, 0]

// Passos: 600 = primária; 50–500 clareiam (mistura com branco); 700–950
// escurecem (mistura com preto). Fatores calibrados p/ uma rampa agradável.
const CLAREAR: Record<number, number> = { 50: 0.92, 100: 0.82, 200: 0.65, 300: 0.45, 400: 0.27, 500: 0.12 }
const ESCURECER: Record<number, number> = { 700: 0.28, 800: 0.42, 900: 0.56, 950: 0.74 }
const PASSOS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const

/** Rampa "R G B" por passo, derivada da cor primária. */
export function derivarRampaBrand(primariaHex: string): Record<number, string> | null {
  const base = hexParaRGB(primariaHex)
  if (!base) return null
  const rampa: Record<number, string> = {}
  for (const p of PASSOS) {
    let cor: RGB
    if (p < 600) cor = mix(base, BRANCO, CLAREAR[p])
    else if (p === 600) cor = base
    else cor = mix(base, PRETO, ESCURECER[p])
    rampa[p] = `${cor[0]} ${cor[1]} ${cor[2]}`
  }
  return rampa
}

/** Marca visível do tenant a aplicar (subset do branding relevante ao tema). */
export interface TemaTenant {
  corPrimaria?: string
  faviconEmoji?: string
  tituloNavegador?: string
}

/**
 * Aplica o tema no documento. Idempotente. Só sobrescreve a rampa quando a
 * primária difere do Azul Patrium (senão mantém a rampa afinada do tokens.css).
 * `doc` injetável p/ teste.
 */
export function aplicarTema(t: TemaTenant, doc: Document = document): void {
  const primaria = (t.corPrimaria ?? '').trim().toUpperCase()
  if (primaria && primaria !== AZUL_PATRIUM) {
    const rampa = derivarRampaBrand(primaria)
    if (rampa) {
      for (const p of PASSOS) doc.documentElement.style.setProperty(`--brand-${p}`, rampa[p])
    }
  }
  if (t.tituloNavegador && t.tituloNavegador.trim()) {
    doc.title = t.tituloNavegador.trim()
  }
  if (t.faviconEmoji && t.faviconEmoji.trim()) {
    aplicarFaviconEmoji(t.faviconEmoji.trim(), doc)
  }
}

// ---------------------------------------------------------------------------
// Preview de contraste (PR2 — marca-tenant-form). Mesma fórmula WCAG AA do
// backend (validarContrasteAA em internal/tenant/domain.go), calculada aqui só
// pra feedback imediato de UX — o backend valida de novo no PUT e é quem manda.
// ---------------------------------------------------------------------------

function canalLinear(c: number): number {
  const v = c / 255
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
}

function luminancia([r, g, b]: RGB): number {
  return 0.2126 * canalLinear(r) + 0.7152 * canalLinear(g) + 0.0722 * canalLinear(b)
}

/** Razão de contraste (≥1) entre uma cor hex e texto branco, ou null se hex ausente/inválido. */
export function razaoContrasteComBranco(hex: string | undefined | null): number | null {
  const rgb = hexParaRGB(hex)
  if (!rgb) return null
  const lCor = luminancia(rgb)
  const lBranco = luminancia([255, 255, 255])
  const [maior, menor] = lBranco >= lCor ? [lBranco, lCor] : [lCor, lBranco]
  return (maior + 0.05) / (menor + 0.05)
}

export const RAZAO_AA = 4.5

/** true se a cor passa WCAG AA (≥4.5:1) contra texto branco. */
export function passaContrasteAA(hex: string | undefined | null): boolean {
  const r = razaoContrasteComBranco(hex)
  return r !== null && r >= RAZAO_AA
}

function aplicarFaviconEmoji(emoji: string, doc: Document): void {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">${emoji}</text></svg>`
  const href = `data:image/svg+xml,${encodeURIComponent(svg)}`
  let link = doc.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  if (!link) {
    link = doc.createElement('link')
    link.rel = 'icon'
    doc.head.appendChild(link)
  }
  link.href = href
}
